import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Container, Alert } from 'reactstrap';
import { useFormik } from 'formik';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';

import {
  defaultIncidentReport,
  validatePublicIncidentReport,
} from '../Generate/Components/FormikHelpers';

import { GenerateIncidentReportForm } from './GenerateIncidentReportForm';
import { SERVER_URL } from '../../../ServerLink';
import { useFetch } from '../../../blog/hooks/useFetch';

import '../Stylesheets/incident-report.scss';
import '../Stylesheets/incident-report-public.scss';
import { setLoading } from '../../../../store/actions';

function buildPersonFromPrefill(p) {
  console.log(p)
  return {
    type: p.type ?? '',
    specifyType: p.specifyType ?? '',
    firstName: p.firstName ?? '',
    middleName: p.middleName ?? '',
    lastName: p.lastName ?? '',
    email: p.email ?? '',
    phoneNumbers: Array.isArray(p.phoneNumbers) ? p.phoneNumbers : (p.phoneNo || p.phone ? [p.phoneNo || p.phone] : []),
    unitNo: p.unitNo ?? '',
    homeAddress: p.homeAddress ?? p.address ?? '',
    notificationToPropertyManagerDateTime: p.notificationToPropertyManagerDateTime ?? p.incidentDetails?.notificationToPropertyManagerDateTime ?? null,
    statement: p.statement ?? p.incidentDetails?.description ?? '',
    attachments: p.attachments ?? p.incidentDetails?.attachments ?? [],
    signature: {
      signature: p.signature?.sign ?? p.signature?.signature ?? '',
      date: p.signature?.date ?? null,
    },
  };
}

function buildValuesFromReport(report, base, recipientEmail) {
  if (!report) return base;

  const persons = Array.isArray(report.notification?.personInvolved)
    ? report.notification.personInvolved
    : [];

  const matchedPerson = recipientEmail
    ? persons.find((p) => p.email?.toLowerCase() === recipientEmail.toLowerCase())
    : persons[0];

  const personInvolved = matchedPerson
    ? [buildPersonFromPrefill(matchedPerson)]
    : persons.length > 0
      ? persons.map(buildPersonFromPrefill)
      : base.notification.personInvolved;

  return {
    ...base,
    _id: report._id,
    propertyDetails: report.propertyDetails ?? base.propertyDetails,
    notification: {
      ...base.notification,
      managerReporting: report.notification?.managerReporting ?? base.notification.managerReporting,
      managerEmail: report.notification?.managerEmail ?? base.notification.managerEmail,
      managerPhoneNo: report.notification?.managerPhoneNo ?? report.notification?.managerphoneNo ?? base.notification.managerPhoneNo,
      personsInvolved: personInvolved,
    },
  };
}

function buildValuesFromPrefill(preFill, base) {
  const notificationPrefill = preFill?.notification ?? {};
  return {
    ...base,
    propertyDetails: preFill?.propertyDetails ?? base.propertyDetails,
    notification: {
      ...base.notification,
      managerReporting: notificationPrefill.managerReporting ?? base.notification.managerReporting ?? '',
      managerEmail: notificationPrefill.managerEmail ?? base.notification.managerEmail ?? '',
      managerPhoneNo: notificationPrefill.managerPhoneNo ?? notificationPrefill.managerphoneNo ?? base.notification.managerPhoneNo ?? '',
      personsInvolved:
        Array.isArray(notificationPrefill.personInvolved) && notificationPrefill.personInvolved.length > 0
          ? notificationPrefill.personInvolved.map(buildPersonFromPrefill)
          : Array.isArray(notificationPrefill.personsInvolved) && notificationPrefill.personsInvolved.length > 0
            ? notificationPrefill.personsInvolved.map(buildPersonFromPrefill)
            : base.notification.personsInvolved,
    },
  };
}

function normalisePerson(person) {
  const { signature, ...rest } = person;
  return {
    ...rest,
    signature: {
      signature: signature?.signature ?? '',
      date: signature?.date ?? null,
    },
  };
}

function buildSubmitPayload(token, values) {
  const person = values.notification?.personsInvolved?.[0] ?? values.notification?.personInvolved?.[0] ?? {};
  const normPerson = normalisePerson(person);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  return {
    token,
    timezone,
    manager: {
      managerReporting: (values.notification?.managerReporting || '').trim(),
      managerEmail: (values.notification?.managerEmail || '').trim(),
      managerPhoneNo: (values.notification?.managerPhoneNo || values.notification?.managerphoneNo || '').trim(),
    },
    personData: normPerson,
  };
}

const SuccessScreen = ({ propertyTitle, companyLogo }) => (
  <div style={{
    minHeight: '100vh',
    background: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: "'Inter', -apple-system, sans-serif",
  }}>
    <div style={{ textAlign: 'center', maxWidth: 400, width: '100%' }}>
      <div style={{
        width: 52, height: 52, borderRadius: '50%',
        background: '#ecfdf5', border: '0.5px solid #6ee7b7',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 20px',
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>

      <p style={{ fontSize: 18, fontWeight: 600, color: '#1e293b', margin: '0 0 6px' }}>
        Incident Report Submitted
      </p>
      <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 28px', lineHeight: 1.6 }}>
        {propertyTitle ? `Pending Management Review — ${propertyTitle}.` : 'Pending Management Review.'}
      </p>

      {companyLogo && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <div style={{ height: '1.5px', width: '40px', background: '#e2e8f0', borderRadius: 2 }} />
          <img
            src={companyLogo}
            alt="Company Logo"
            style={{
              maxHeight: '40px',
              maxWidth: '180px',
              objectFit: 'contain',
              filter: 'grayscale(0.2) opacity(0.8)'
            }}
          />
        </div>
      )}
      <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 40px' }}>
        You may now close this tab.
      </p>
    </div>
  </div>
);
const GeneratePublicIncidentReport = () => {
  const dispatch = useDispatch();

  const { token: routeToken } = useParams();
  const location = useLocation();
  const queryToken = new URLSearchParams(location.search).get('invite');
  const token = routeToken || queryToken;

  const [valid, setValid] = useState(false);
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [preFill, setPreFill] = useState({});
  const [existingReport, setExistingReport] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState(null);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reportId, setReportId] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);

  const handleValidateSuccess = useCallback((res) => {
    if (res?.success && res.data) {
      const data = res.data;
      setInviteData(data);
      setPreFill(data.dataToPrefill || {});
      setRecipientEmail(data.personEmail ?? null);
      setAlreadyConfirmed(data.isConfirmed === true);

      if (data.report) {
        setExistingReport(data.report);
        setReportId(data.report._id ?? data.report.id ?? null);
      } else if (data.reportId) {
        setReportId(data.reportId);
      }

      if (data.companyLogo) {
        setCompanyLogo(data.companyLogo);
      }

      setValid(true);
    } else {
      setError(res?.message || 'Invalid or expired invitation');
    }
    setIsVerifying(false);
    dispatch(setLoading(false));
  }, [dispatch]);

  const handleValidateError = useCallback((err) => {
    setError(err?.message || 'Could not validate invitation');
    setIsVerifying(false);
    dispatch(setLoading(false));
  }, [dispatch]);

  const fetchParams = useMemo(() => (token ? { token } : null), [token]);

  useFetch({
    url: `${SERVER_URL}/v2/incident-reports/public/invite/validate`,
    params: fetchParams,
    skip: !token,
    onSuccess: handleValidateSuccess,
    onError: handleValidateError,
  });

  useEffect(() => {
    if (token) {
      dispatch(setLoading(true));
      setIsVerifying(true);
    } else {
      setError('Missing or invalid invitation link. Please use the link sent to your email.');
      setIsVerifying(false);
      dispatch(setLoading(false));
    }
  }, [token, dispatch]);

  const initialValues = useMemo(() => {
    const base = defaultIncidentReport;
    if (existingReport) return buildValuesFromReport(existingReport, base, recipientEmail);
    return buildValuesFromPrefill(preFill, base);
  }, [existingReport, preFill, recipientEmail]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values, { setSubmitting }) => {
      dispatch(setLoading(true));
      setSubmitting(true);

      try {
        const payload = buildSubmitPayload(token, values);

        const response = await fetch(`${SERVER_URL}/v2/incident-reports/public/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Server error (${response.status})`);
        }

        const res = await response.json();
        if (res.success) {
          if (res.data?.reportId) setReportId(res.data.reportId);
          if (res.data?.companyLogo) setCompanyLogo(res.data.companyLogo);
          setAlreadyConfirmed(true);
          setSubmitted(true);
        } else {
          throw new Error(res.message || 'Submission failed');
        }
      } catch (err) {
        Swal.fire('Error', err.message || 'Failed to submit report.', 'error');
        console.error('Submission error:', err);
      } finally {
        dispatch(setLoading(false));
        setSubmitting(false);
      }
    },
  });

  const handleSubmit = useCallback(async () => {
    const isValid = await validatePublicIncidentReport(formik.values);
    if (!isValid) return;
    formik.handleSubmit();
  }, [formik]);

  if (isVerifying) {
    return (
      <Container className="py-5 text-center">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div>
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
            <h5>Verifying invitation...</h5>
            <p className="text-muted">Please wait while we validate your link</p>
          </div>
        </div>
      </Container>
    );
  }

  if (error || !valid) {
    return (
      <Container className="py-5">
        <Alert color="danger">
          {error || 'This invitation link is invalid or has expired.'}
          <br />
          <small>Please contact the property management team for assistance.</small>
        </Alert>
      </Container>
    );
  }

  if (submitted) {
    return (
      <SuccessScreen
        propertyTitle={inviteData?.propertyTitle}
        companyLogo={companyLogo}
      />
    );
  }

  const sharedProps = {
    formik,
    onSubmit: handleSubmit,
    isSubmitting: formik.isSubmitting,
    token,
    tokenInfo: inviteData || {},
    reportId,
    expiresAt: inviteData?.expiresAt,
    propertyTitle: inviteData?.propertyTitle,
    referenceId: inviteData?.referenceId,
    invitedBy: inviteData?.invitedBy,
    companyLogo: companyLogo,
    visibleSections: [1],
  };

  if (alreadyConfirmed && (existingReport || reportId)) {
    return (
      <GenerateIncidentReportForm
        {...sharedProps}
        mode="preview"
        alreadyConfirmedBanner
      />
    );
  }

  return <GenerateIncidentReportForm {...sharedProps} mode="initial" />;
};

export { GeneratePublicIncidentReport };